import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Paperclip,
  Search,
  RefreshCw,
  Eye,
  Trash2,
  CheckCircle2,
  Mail,
  Loader2,
  History,
  AlertTriangle,
} from "lucide-react";
import UserMenu from "../components/UserMenu";
import ClassifyAttachmentDialog from "../components/ClassifyAttachmentDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const formatBytes = (n) => {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }); } catch { return s; }
};

export default function PendingAttachments({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [projectFilter, setProjectFilter] = useState(searchParams.get("project_id") || "all");
  const [search, setSearch] = useState(searchParams.get("q") || "");

  const [selectedKeys, setSelectedKeys] = useState(new Set()); // `${email_id}:${idx}`

  const [classifyTarget, setClassifyTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDocType, setBulkDocType] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const [discardTarget, setDiscardTarget] = useState(null);
  const [discardReason, setDiscardReason] = useState("");
  const [discardSubmitting, setDiscardSubmitting] = useState(false);

  // Per-row preview loading state
  const [previewingKey, setPreviewingKey] = useState(null);  // `${email_id}:${idx}`

  // Backfill (one-shot historical re-fetch — runs as background job + polled)
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillMailboxes, setBackfillMailboxes] = useState([]);
  const [backfillMailboxId, setBackfillMailboxId] = useState("all");
  const [backfillIncludeEmpty, setBackfillIncludeEmpty] = useState(false);
  const [dryRunStarting, setDryRunStarting] = useState(false);
  const [realRunStarting, setRealRunStarting] = useState(false);
  const [backfillJob, setBackfillJob] = useState(null);  // {id, status, dry_run, progress: {...}}
  const [pollTimer, setPollTimer] = useState(null);

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  // Sync URL state
  useEffect(() => {
    const params = {};
    if (projectFilter && projectFilter !== "all") params.project_id = projectFilter;
    if (search) params.q = search;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line
  }, [projectFilter, search]);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const params = {};
      if (projectFilter && projectFilter !== "all") params.project_id = projectFilter;
      if (search) params.search = search;
      const res = await axios.get(`${API}/email/attachments/pending`, { ...auth(), params });
      setItems(res.data.items || []);
      setProjects(res.data.projects || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pending attachments");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // refetch when filters change
  useEffect(() => {
    if (loading) return;
    fetchData();
    // eslint-disable-next-line
  }, [projectFilter]);

  // search debounce
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const groupedByProject = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      const pid = it.project_id || "_unassigned";
      if (!map[pid]) map[pid] = { project: it.project, items: [] };
      map[pid].items.push(it);
    });
    return Object.entries(map).map(([pid, val]) => ({ pid, ...val }));
  }, [items]);

  const totalSize = useMemo(
    () => items.reduce((s, it) => s + (it.size || 0), 0),
    [items]
  );

  const isSelected = (it) => selectedKeys.has(`${it.email_id}:${it.attachment_index}`);
  const toggleSelect = (it) => {
    const k = `${it.email_id}:${it.attachment_index}`;
    setSelectedKeys((prev) => {
      const ns = new Set(prev);
      if (ns.has(k)) ns.delete(k); else ns.add(k);
      return ns;
    });
  };
  const selectAllInGroup = (g) => {
    setSelectedKeys((prev) => {
      const ns = new Set(prev);
      const allSelected = g.items.every((it) => ns.has(`${it.email_id}:${it.attachment_index}`));
      g.items.forEach((it) => {
        const k = `${it.email_id}:${it.attachment_index}`;
        if (allSelected) ns.delete(k); else ns.add(k);
      });
      return ns;
    });
  };

  const selectedItems = useMemo(
    () => items.filter((it) => isSelected(it)),
    // eslint-disable-next-line
    [items, selectedKeys]
  );
  const selectedProjectIds = useMemo(
    () => new Set(selectedItems.map((it) => it.project_id)),
    [selectedItems]
  );

  const handlePreview = async (it) => {
    const key = `${it.email_id}:${it.attachment_index}`;
    if (previewingKey) return;  // ignore double-clicks while one is in-flight
    setPreviewingKey(key);
    const loadingToast = toast.loading("Opening preview…");
    // Pop the new tab synchronously so popup blockers don't fire
    const newTab = window.open("", "_blank");
    try {
      const res = await axios.get(
        `${API}/email/attachments/${it.email_id}/${it.attachment_index}/preview-url`,
        auth()
      );
      const url = res.data?.url;
      if (!url) throw new Error("No URL returned");
      if (newTab && !newTab.closed) {
        newTab.location.href = url;
      } else {
        // Popup was blocked — fall back to current-window navigation
        window.location.href = url;
      }
      toast.success("Preview opened", { id: loadingToast });
    } catch (e) {
      if (newTab && !newTab.closed) newTab.close();
      toast.error(e?.response?.data?.detail || "Failed to open preview", { id: loadingToast });
    } finally {
      setPreviewingKey(null);
    }
  };

  const submitDiscard = async () => {
    if (!discardTarget) return;
    setDiscardSubmitting(true);
    try {
      await axios.post(
        `${API}/email/attachments/${discardTarget.email_id}/${discardTarget.attachment_index}/discard`,
        { reason: discardReason || null },
        auth()
      );
      toast.success("Attachment discarded");
      setDiscardTarget(null);
      setDiscardReason("");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to discard");
    } finally {
      setDiscardSubmitting(false);
    }
  };

  // Backfill handlers
  const openBackfill = async () => {
    setBackfillJob(null);
    setBackfillMailboxId("all");
    setBackfillOpen(true);
    try {
      const res = await axios.get(`${API}/email/settings/all`, auth());
      setBackfillMailboxes(res.data?.mailboxes || []);
    } catch {
      setBackfillMailboxes([]);
    }
    // Resume showing progress if a job is currently running or recently finished
    try {
      const ar = await axios.get(`${API}/email/attachments/backfill/active`, auth());
      if (ar.data && ar.data.id) {
        setBackfillJob(ar.data);
        if (ar.data.status === "running") startPolling(ar.data.id);
      }
    } catch { /* ignore */ }
  };

  const startPolling = (jobId) => {
    if (pollTimer) clearInterval(pollTimer);
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/email/attachments/backfill/${jobId}`, auth());
        setBackfillJob(r.data);
        if (r.data.status !== "running") {
          clearInterval(t);
          setPollTimer(null);
          setDryRunStarting(false);
          setRealRunStarting(false);
          if (r.data.status === "completed") {
            const totals = r.data.progress || {};
            if (!r.data.dry_run) {
              toast.success(`Backfilled ${totals.total_attachments_uploaded || 0} attachment(s) across ${totals.total_emails_patched || 0} email(s)`);
              fetchData();
            } else {
              toast.success(`Dry run complete — ${totals.total_emails_inspected || 0} email(s) would be inspected`);
            }
          } else if (r.data.status === "failed") {
            toast.error(`Backfill failed: ${r.data.error || "unknown error"}`);
          }
        }
      } catch {
        // job may have been deleted; stop polling
        clearInterval(t);
        setPollTimer(null);
      }
    }, 2000);
    setPollTimer(t);
  };

  const runBackfill = async (dryRun) => {
    if (dryRun) setDryRunStarting(true);
    else setRealRunStarting(true);
    setBackfillJob(null);
    try {
      const params = { limit_per_mailbox: 2000 };
      if (dryRun) params.dry_run = true;
      if (backfillIncludeEmpty) params.include_empty_attachments = true;
      if (backfillMailboxId !== "all") params.mailbox_id = backfillMailboxId;
      const res = await axios.post(
        `${API}/email/attachments/backfill`,
        null,
        { ...auth(), params }
      );
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error("No job_id returned");
      // Initial poll immediately
      const r = await axios.get(`${API}/email/attachments/backfill/${jobId}`, auth());
      setBackfillJob(r.data);
      if (r.data.status === "running") {
        startPolling(jobId);
      } else {
        setDryRunStarting(false);
        setRealRunStarting(false);
      }
    } catch (e) {
      setDryRunStarting(false);
      setRealRunStarting(false);
      toast.error(e?.response?.data?.detail || e.message || "Failed to start backfill");
    }
  };

  // Cleanup poll on close/unmount
  useEffect(() => {
    return () => { if (pollTimer) clearInterval(pollTimer); };
  }, [pollTimer]);

  const submitBulk = async () => {
    if (!bulkDocType) {
      toast.error("Choose a doc type");
      return;
    }
    if (selectedProjectIds.size > 1) {
      toast.error("Bulk classify requires all selected to be in the same project");
      return;
    }
    setBulkSubmitting(true);
    try {
      const targets = selectedItems.map((it) => ({
        email_id: it.email_id,
        attachment_index: it.attachment_index,
      }));
      const res = await axios.post(
        `${API}/email/attachments/bulk-classify`,
        { targets, doc_type: bulkDocType, mode: "new_version" },
        auth()
      );
      const { classified_count, errors } = res.data;
      toast.success(`Classified ${classified_count} attachment(s)`);
      if ((errors || []).length) {
        toast.error(`${errors.length} error(s) — see console`);
        console.warn("Bulk classify errors", errors);
      }
      setBulkOpen(false);
      setBulkDocType("");
      setSelectedKeys(new Set());
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk classify failed");
    } finally {
      setBulkSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  }

  return (
    <div data-testid="pending-attachments-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="erp-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Paperclip className="w-6 h-6" /> Pending Email Attachments
              </h1>
              <p className="text-blue-100 text-sm mt-0.5">
                Classify incoming attachments as project documents
              </p>
            </div>
          </div>
          <UserMenu user={user} onLogout={onLogout} variant="header" />
        </div>
      </div>

      <div className="erp-container">
        {/* Stats + actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="erp-card">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Pending</div>
            <div className="text-3xl font-bold text-blue-700 mt-1" data-testid="stat-total-pending">{items.length}</div>
          </div>
          <div className="erp-card">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Across Projects</div>
            <div className="text-3xl font-bold text-gray-800 mt-1">{groupedByProject.length}</div>
          </div>
          <div className="erp-card">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total Size</div>
            <div className="text-3xl font-bold text-gray-800 mt-1">{formatBytes(totalSize)}</div>
          </div>
          <div className="erp-card flex flex-col items-center justify-center gap-2">
            <Button
              onClick={fetchData}
              disabled={refreshing}
              variant="outline"
              className="w-full"
              data-testid="refresh-btn"
            >
              {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
            <Button
              onClick={openBackfill}
              variant="ghost"
              className="w-full text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              data-testid="backfill-open-btn"
              title="Re-fetch attachments from IMAP for emails synced before this feature shipped"
            >
              <History className="w-4 h-4 mr-2" />
              Backfill historical
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="erp-card mb-4 flex flex-col md:flex-row gap-3 items-stretch md:items-end">
          <div className="flex-1">
            <Label className="text-xs">Search</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                placeholder="filename / sender / subject"
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="md:w-72">
            <Label className="text-xs">Project</Label>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="mt-1" data-testid="project-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedKeys.size > 0 && (
            <div className="flex gap-2 items-center">
              <span className="text-sm text-gray-600" data-testid="selected-count">
                {selectedKeys.size} selected
              </span>
              <Button
                onClick={() => setBulkOpen(true)}
                disabled={selectedProjectIds.size > 1}
                title={selectedProjectIds.size > 1 ? "Selected items must belong to the same project" : ""}
                data-testid="bulk-classify-btn"
              >
                Bulk Classify
              </Button>
              <Button
                variant="ghost"
                onClick={() => setSelectedKeys(new Set())}
                data-testid="clear-selection-btn"
              >
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* Empty state */}
        {items.length === 0 && (
          <div className="erp-card text-center py-12" data-testid="empty-state no-rows-empty-state">
            <CheckCircle2 className="w-10 h-10 mx-auto text-green-500 mb-3" />
            <p className="text-gray-700 font-medium">No pending attachments</p>
            <p className="text-sm text-gray-500 mt-1">All incoming attachments have been classified or discarded.</p>
          </div>
        )}

        {/* Groups */}
        <div className="space-y-4">
          {groupedByProject.map((g) => {
            const allSelected = g.items.every((it) => isSelected(it));
            return (
              <div key={g.pid} className="erp-card" data-testid={`project-group-${g.pid}`}>
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">
                      {g.project?.name || "Unassigned"}
                      <span className="text-sm text-gray-500 font-normal ml-2">
                        ({g.items.length} pending)
                      </span>
                    </div>
                    {g.project?.id && (
                      <button
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => navigate(`/project/${g.project.id}/emails`)}
                        data-testid={`open-project-emails-${g.project.id}`}
                      >
                        Open project emails →
                      </button>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => selectAllInGroup(g)}
                    data-testid={`select-all-group-${g.pid}`}
                  >
                    {allSelected ? "Unselect all" : "Select all"}
                  </Button>
                </div>

                <table className="erp-table w-full">
                  <thead>
                    <tr>
                      <th className="w-8"></th>
                      <th>File</th>
                      <th>Size</th>
                      <th>From</th>
                      <th>Subject</th>
                      <th>Date</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((it) => (
                      <tr key={`${it.email_id}-${it.attachment_index}`} data-testid={`pending-row-${it.email_id}-${it.attachment_index}`}>
                        <td>
                          <Checkbox
                            checked={isSelected(it)}
                            onCheckedChange={() => toggleSelect(it)}
                            data-testid={`select-${it.email_id}-${it.attachment_index}`}
                          />
                        </td>
                        <td className="font-mono text-sm">
                          <div className="flex items-center gap-2">
                            <Paperclip className="w-3 h-3 text-gray-400 shrink-0" />
                            <span className="truncate max-w-[280px]" title={it.filename}>{it.filename}</span>
                          </div>
                        </td>
                        <td className="text-xs text-gray-600 whitespace-nowrap">{formatBytes(it.size)}</td>
                        <td className="text-xs text-gray-600 truncate max-w-[200px]">
                          {it.from?.email || ""}
                        </td>
                        <td className="text-xs text-gray-700 truncate max-w-[280px]" title={it.subject}>
                          <Mail className="w-3 h-3 inline text-blue-400 mr-1" />
                          {it.subject}
                        </td>
                        <td className="text-xs text-gray-500 whitespace-nowrap">{formatDate(it.email_date)}</td>
                        <td className="text-right whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreview(it)}
                            disabled={!it.downloaded || !!previewingKey}
                            title={it.downloaded ? "Preview" : "Re-sync mailbox to fetch this attachment"}
                            data-testid={`preview-btn-${it.email_id}-${it.attachment_index}`}
                          >
                            {previewingKey === `${it.email_id}:${it.attachment_index}` ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setClassifyTarget(it)}
                            disabled={!it.downloaded}
                            className="ml-1"
                            data-testid={`classify-btn-${it.email_id}-${it.attachment_index}`}
                          >
                            Classify
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDiscardTarget(it)}
                            className="ml-1 text-red-600 hover:text-red-700"
                            data-testid={`discard-btn-${it.email_id}-${it.attachment_index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>

      {/* Classify Dialog */}
      <ClassifyAttachmentDialog
        open={!!classifyTarget}
        target={classifyTarget}
        onClose={() => setClassifyTarget(null)}
        onClassified={fetchData}
      />

      {/* Discard Dialog */}
      <Dialog open={!!discardTarget} onOpenChange={(o) => { if (!o) { setDiscardTarget(null); setDiscardReason(""); } }}>
        <DialogContent data-testid="discard-attachment-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Discard Attachment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2 text-sm">
            <p>
              You are about to <span className="font-bold">permanently delete</span> the file{" "}
              <span className="font-mono">{discardTarget?.filename}</span> from storage.
            </p>
            <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              This is irreversible. The email row remains, but the file bytes will be erased from R2.
            </p>
            <div>
              <Label className="text-sm">Reason (optional)</Label>
              <Textarea
                value={discardReason}
                onChange={(e) => setDiscardReason(e.target.value)}
                rows={2}
                className="mt-1"
                placeholder="e.g., signature image / spam attachment"
                data-testid="discard-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDiscardTarget(null)} disabled={discardSubmitting} data-testid="discard-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={submitDiscard}
              disabled={discardSubmitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="discard-confirm-btn"
            >
              {discardSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backfill Dialog */}
      <Dialog open={backfillOpen} onOpenChange={(o) => { if (!dryRunStarting && !realRunStarting && backfillJob?.status !== "running") setBackfillOpen(o); }}>
        <DialogContent className="max-w-3xl" data-testid="backfill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-amber-600" />
              Backfill Historical Attachments
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2 text-sm">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-900 flex gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Re-fetches attachment files from your mail server for old emails</p>
                <p className="text-xs mt-1">Runs as a background job — safe to close this dialog while it works. Re-open to see progress. Idempotent: already-backfilled emails are skipped.</p>
              </div>
            </div>

            <div>
              <Label className="text-sm">Mailbox</Label>
              <Select
                value={backfillMailboxId}
                onValueChange={setBackfillMailboxId}
                disabled={dryRunStarting || realRunStarting || backfillJob?.status === "running"}
              >
                <SelectTrigger className="mt-1" data-testid="backfill-mailbox-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All configured mailboxes</SelectItem>
                  {backfillMailboxes.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="backfill-include-empty-toggle">
              <input
                type="checkbox"
                checked={backfillIncludeEmpty}
                onChange={(e) => setBackfillIncludeEmpty(e.target.checked)}
                disabled={dryRunStarting || realRunStarting || backfillJob?.status === "running"}
                className="mt-1 w-4 h-4"
              />
              <div>
                <div className="font-medium text-gray-800">Include emails currently showing 0 attachments</div>
                <div className="text-xs text-gray-500">
                  Use this for emails sent via Outlook desktop / external clients that synced without attachment metadata.
                  Slower (rebuilds attachment list from IMAP), so leave off unless needed.
                </div>
              </div>
            </label>

            {backfillJob && (
              <div className="border border-gray-200 rounded p-3 bg-gray-50" data-testid="backfill-result">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">
                    {backfillJob.status === "running" ? (
                      <span className="flex items-center gap-2 text-blue-700">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {backfillJob.dry_run ? "Dry run in progress…" : "Backfill in progress…"}
                      </span>
                    ) : backfillJob.status === "completed" ? (
                      <span className="text-green-700">
                        ✓ {backfillJob.dry_run ? "Dry run complete" : "Backfill complete"}
                      </span>
                    ) : (
                      <span className="text-red-600">✕ Backfill failed</span>
                    )}
                  </span>
                  <span className="text-xs text-gray-500">
                    {(backfillJob.progress?.total_attachments_uploaded ?? 0)} attachment(s) · {(backfillJob.progress?.total_emails_patched ?? 0)} email(s) · {(backfillJob.progress?.total_errors ?? 0)} error(s)
                  </span>
                </div>

                {backfillJob.status === "running" && backfillJob.progress?.mailbox_total > 0 && (
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Mailbox {backfillJob.progress.mailbox_index} of {backfillJob.progress.mailbox_total}{backfillJob.progress.current_mailbox ? ` — ${backfillJob.progress.current_mailbox}` : ""}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded overflow-hidden">
                      <div
                        className="h-full bg-amber-500 transition-all duration-300"
                        style={{ width: `${Math.round((backfillJob.progress.mailbox_index / Math.max(backfillJob.progress.mailbox_total, 1)) * 100)}%` }}
                        data-testid="backfill-progress-bar"
                      />
                    </div>
                  </div>
                )}

                {backfillJob.error && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2">
                    {backfillJob.error}
                  </div>
                )}

                {(backfillJob.progress?.mailboxes || []).length > 0 && (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-600 border-b">
                        <th className="py-1">Mailbox</th>
                        <th>Inspected</th>
                        <th>Patched</th>
                        <th>Uploaded</th>
                        <th>Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(backfillJob.progress?.mailboxes || []).map((mb, i) => (
                        <tr key={i} className="border-b last:border-0" data-testid={`backfill-mb-row-${i}`}>
                          <td className="py-1 font-mono">{mb.mailbox}</td>
                          <td>{mb.emails_inspected}</td>
                          <td>{mb.emails_patched}</td>
                          <td>{mb.attachments_uploaded}</td>
                          <td>
                            {mb.errors?.length ? (
                              <span className="text-red-600" title={mb.errors.join(' | ')}>{mb.errors.length}</span>
                            ) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="ghost"
              onClick={() => setBackfillOpen(false)}
              disabled={dryRunStarting || realRunStarting}
              data-testid="backfill-close-btn"
            >
              Close
            </Button>
            <Button
              variant="outline"
              onClick={() => runBackfill(true)}
              disabled={dryRunStarting || realRunStarting || backfillJob?.status === "running"}
              data-testid="backfill-dryrun-btn"
            >
              {dryRunStarting || (backfillJob?.status === "running" && backfillJob.dry_run) ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Dry Run
            </Button>
            <Button
              onClick={() => runBackfill(false)}
              disabled={dryRunStarting || realRunStarting || backfillJob?.status === "running"}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="backfill-run-btn"
            >
              {realRunStarting || (backfillJob?.status === "running" && !backfillJob.dry_run) ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Run Backfill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-classify-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Classify {selectedKeys.size} attachment(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2 text-sm">
            <p>
              All selected attachments must belong to the <span className="font-bold">same project</span>.
              Each will be saved as a new version group with the chosen Document Type.
            </p>
            <div>
              <Label className="text-sm">Document Type</Label>
              <Select value={bulkDocType} onValueChange={setBulkDocType}>
                <SelectTrigger className="mt-1" data-testid="bulk-doc-type-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {["Drawing", "BOM", "Purchase Order", "Quotation", "Invoice", "Mezzanine", "Design", "Client Docs", "Others"].map((dt) => (
                    <SelectItem key={dt} value={dt}>{dt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkOpen(false)} disabled={bulkSubmitting} data-testid="bulk-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submitBulk} disabled={bulkSubmitting || !bulkDocType} data-testid="bulk-submit-btn">
              {bulkSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Classify {selectedKeys.size}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
